import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  Linking,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { Button } from "@/components/Button";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { Colors } from "@/constants/Colors";
import Images from "@/assets/images";
import { Image } from "expo-image";
import { StyleSheet, scale } from "@/utils/appStyleSheet";

const OutOfFreeClaimScreen = () => {
  const [selectedOption, setSelectedOption] = useState<"invite" | "upgrade">(
    "upgrade"
  );

  const handleInviteFriend = () => {
    setSelectedOption("invite");
  };

  const handleUpgrade = () => {
    setSelectedOption("upgrade");
  };

  const handleContinue = () => {
    if (selectedOption === "invite") {
      router.push("/refer");
    } else if (selectedOption === "upgrade") {
      router.push("/subscription/upgrade");
    }
  };

  const handleClose = () => {
    router.back();
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Close Button */}
        <View>
          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <Ionicons name="close" size={scale(32)} color="#000" />
          </TouchableOpacity>
          <Image
            source={Images.announcement}
            style={styles.announcement}
            resizeMode="contain"
          />
          {/* Title and Message */}
          <Text style={styles.title}>You&apos;re out of free claims</Text>
          <Text style={styles.message}>
            You&apos;ve used your 2 free claims this month.{"\n"}
            Choose how to keep filing:
          </Text>
          {/* Options */}
          <View style={styles.options}>
            {/* Invite Friend Option */}
            <TouchableOpacity
              style={[
                styles.optionCard,
                selectedOption === "invite" && styles.optionCardSelected,
              ]}
              onPress={handleInviteFriend}
              activeOpacity={0.8}
            >
              <View style={styles.optionContent}>
                <View
                  style={[
                    styles.radioButton,
                    selectedOption === "invite" && styles.radioButtonActive,
                  ]}
                >
                  {selectedOption === "invite" && (
                    <View style={styles.radioButtonSelected} />
                  )}
                </View>
                <View style={styles.optionText}>
                  <View style={styles.optionHeader}>
                    <Text style={styles.optionTitle}>Invite a Friend</Text>
                    <Text style={styles.optionBenefit}>+1 Free Claim</Text>
                  </View>
                  <Text style={styles.optionSubtitle}>
                    Earn 1 extra claim for each friend signs up
                  </Text>
                </View>
              </View>
            </TouchableOpacity>

            {/* Upgrade Option */}
            <TouchableOpacity
              style={[
                styles.optionCard,
                selectedOption === "upgrade" && styles.optionCardSelected,
              ]}
              onPress={handleUpgrade}
              activeOpacity={0.8}
            >
              <View style={styles.optionContent}>
                <View
                  style={[
                    styles.radioButton,
                    selectedOption === "upgrade" && styles.radioButtonActive,
                  ]}
                >
                  {selectedOption === "upgrade" && (
                    <View style={styles.radioButtonSelected} />
                  )}
                </View>
                <View style={styles.optionText}>
                  <View style={styles.optionHeader}>
                    <Text style={styles.optionTitle}>
                      Upgrade for Unlimited
                    </Text>
                    <Text style={styles.optionPrice}>$7/month</Text>
                  </View>
                  <Text style={styles.optionSubtitle}>
                    File without limits. Cancel anytime.
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          </View>
          {/* Footer Text */}
          <Text style={styles.footerText}>
            Most class actions are free.{"\n"}
            Premium lets you file unlimited claims anytime.
          </Text>
        </View>

        {/* Continue Button */}
        <View>
          <Button
            title="Claim More"
            variant="primary"
            style={styles.claimButton}
            onPress={handleContinue}
          />
          <Text style={styles.termsText}>
            By continuing you agree to our{" "}
            <Text
              style={styles.termsTextLink}
              onPress={() => Linking.openURL("https://usesparrow.com/terms")}
            >
              Terms of Use
            </Text>{" "}
            and{" "}
            <Text
              style={styles.termsTextLink}
              onPress={() => Linking.openURL("https://usesparrow.com/privacy")}
            >
              Privacy Policy
            </Text>
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default OutOfFreeClaimScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  scrollContent: {
    flex: 1,
    paddingHorizontal: 16,
    paddingBottom: 40,
    justifyContent: "space-between",
  },
  closeButton: {
    alignSelf: "flex-end",
    padding: 8,
    marginTop: 8,
  },
  announcement: {
    width: "100%",
    height: 100,
    alignSelf: "center",
    marginBottom: 24,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 28,
    lineHeight: 35,
    color: "#000000",
    textAlign: "center",
    marginBottom: 12,
  },
  message: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#000000",
    textAlign: "center",
    marginBottom: 24,
  },
  options: {},
  optionCard: {
    borderWidth: 1,
    borderColor: "#A3A3A3",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  optionCardSelected: {
    borderColor: Colors.primary,
  },
  optionContent: {
    flexDirection: "row",
    gap: 12,
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#A3A3A3",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 2,
  },
  radioButtonActive: {
    borderColor: Colors.primary,
  },
  radioButtonSelected: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Colors.primary,
  },
  optionText: {
    flex: 1,
  },
  optionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  optionTitle: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 15,
    lineHeight: 23,
    color: "#000000",
  },
  optionSubtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 11,
    lineHeight: 15,
    color: "#000000",
  },
  optionBenefit: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 15,
    lineHeight: 23,
    color: "#000000",
  },
  optionPrice: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 15,
    lineHeight: 23,
    color: "#000000",
  },
  claimButton: {
    marginBottom: 16,
    backgroundColor: Colors.primary,
  },
  footerText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 11,
    lineHeight: 15,
    color: "#414141",
    textAlign: "center",
    marginBottom: 16,
  },
  termsText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 12,
    lineHeight: 15,
    color: "#414141",
    textAlign: "center",
  },
  termsTextLink: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 12,
    lineHeight: 15,
    color: Colors.primary,
  },
});
